import { ColumnsType } from 'antd/es/table/interface';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';

import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { Order, OrderType } from '../models/Orders';
import { AppealTask } from '../models/AppealTask';
import { handleSortClick } from './sorterColumn';
import { ColumnTitle } from '../components/ColumnTitle/ColumnTitle';
import { TableColumnType } from 'antd';

const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A';

export const getAppealTaskColumns = (
	t: TFunction<'translation', undefined, 'translation'>,
	sortBy: string,
	setSortBy: (sortValue: string) => void,
	isMobile: boolean,
) => {
	const getStatusColor = (status: String) => {
		switch (status) {
			case 'OPENED':
				return { label: t<string>('opened'), color: '#40af0c', bgColor: '#c6e7b6' };
			case 'TRANSFERRED':
				return { label: t<string>('transferred'), color: '#09bea8', bgColor: '#e6f6f4' };
			case 'COMPLETED':
				return { label: t<string>('completed'), color: '#33a867', bgColor: '#e9ffe5' };
			case 'APPEAL':
				return { label: t<string>('appealed'), color: '#ffd600', bgColor: '#ffd600' };
			case 'REJECTED':
				return { label: t<string>('rejected'), color: '#f6465d', bgColor: '#ffdde1' };
			case 'EXPIRED':
				return { label: t<string>('expired'), color: '#e88b00', bgColor: '#fadfb7' };
			case 'CANCELLED':
				return { label: t<string>('cancelled'), color: '#707070', bgColor: '#e8e8e8' };
			case 'PLACED':
				return { label: t<string>('placed'), color: '#2da8e8', bgColor: '#b3e5ff' };
			default:
				return { label: t<string>('unknown'), color: '#575757', bgColor: '#FFE7E7' };
		}
	};
	const isDev = window.location.hostname.includes('devb');
	const columns: ColumnsType<AppealTask> = [
		{
			title: (
				<div className=' text-[#000D1D99]  flex items-center cursor-pointer'>
					<ColumnTitle title={t('orderId')} />
					<div className='flex flex-col mx-2 text-xs text-xs mt-1 '>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'orderId-asc'))}
							style={{ color: sortBy === 'orderId-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'orderId-desc'))}
							style={{ color: sortBy === 'orderId-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			width: '12%',
			dataIndex: 'orderId',
			key: 'orderId',
			responsive: ['lg'],
			ellipsis: true,
			render: (value, record) => {
				return (
					<Link
						to={`/order-life-cycle/?id=${record.orderNumber}`}
						className='flex items-center text-[#0ea5e9] underline'
					>
						{record.orderNumber}
					</Link>
				);
			},
		},
		{
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t('client')} />
					<div className='flex flex-col mx-2 text-xs mt-1 '>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'client-asc'))}
							style={{ color: sortBy === 'client-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'client-desc'))}
							style={{ color: sortBy === 'client-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'client',
			key: 'client',
			responsive: ['lg'],
			render: (value, record) => (
				<div className='word-break-column  text-[#01081E99] flex items-center '>{record.clientFullName}</div>
			),
		},
		{
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t('merchants')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'merchant-asc'))}
							style={{ color: sortBy === 'merchant-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'merchant-desc'))}
							style={{ color: sortBy === 'merchant-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'merchants',
			key: 'merchants',
			responsive: ['lg'],
			render: (value, record) => (
				<div className='word-break-column  text-[#01081E99] flex items-center '>{record.merchantFullName}</div>
			),
		},
		{
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t('Type')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'type-asc'))}
							style={{ color: sortBy === 'type-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'type-desc'))}
							style={{ color: sortBy === 'type-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'orderType',
			key: 'orderType',
			responsive: ['lg'],
			render: (value, record) => {
				return (
					<>
						{record.type.toLocaleLowerCase() === OrderType.buy.toLocaleLowerCase() ? (
							<div className='font-medium text-[green] flex items-center'>
								{isDev ? t<string>('buy') : t<string>('deposit')}
							</div>
						) : (
							<div className='font-medium text-[red] flex items-center'>
								{isDev ? t<string>('sell') : t<string>('withdrawal')}
							</div>
						)}
					</>
				);
			},
		},
		{
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t('amount')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'amount-asc'))}
							style={{ color: sortBy === 'amount-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'amount-desc'))}
							style={{ color: sortBy === 'amount-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'amount',
			key: 'amount',
			width: '10%',
			responsive: ['lg'],
			render: (value, record) => (
				<div className=' font-medium text-lightBlue flex items-center'>{record.amount?.toLocaleString()}</div>
			),
			ellipsis: true,
		},
		{
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t('price')} />
					<div className='flex flex-col mx-2 text-xs mt-1 '>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'price-asc'))}
							style={{ color: sortBy === 'price-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'price-desc'))}
							style={{ color: sortBy === 'price-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'price',
			key: 'price',
			width: '10%',
			responsive: ['lg'],
			render: (value, record) => {
				return (
					<div className='font-medium text-lightBlue flex items-center'>
						{record.price?.toLocaleString()}
						<span className='inline-block mx-1 text-[grey]'>USD</span>
					</div>
				);
			},
		},
		{
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t('status')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'status-asc'))}
							style={{ color: sortBy === 'status-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'status-desc'))}
							style={{ color: sortBy === 'status-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'status',
			key: 'status',
			responsive: ['lg'],
			width: '10%',
			render: (value, record) => {
				return (
					<div
						className='flex items-center  flex-wrap rounded-sm font-medium text-[#000] '
						style={{ color: getStatusColor(record.status).color, flexWrap: 'nowrap' }}
					>
						{/* <div
							style={{
								width: '5px',
								height: '5px',
								borderRadius: '100px',
								marginInlineEnd: '5px',
								backgroundColor: getStatusColor(record.status).color,
							}}
						/> */}
						{getStatusColor(record.status).label}
					</div>
				);
			},
		},
		{
			title: (
				<div className='  flex items-center cursor-pointer'>
					<ColumnTitle title={t('Resolved')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'resolved-asc'))}
							style={{ color: sortBy === 'resolved-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'resolved-desc'))}
							style={{ color: sortBy === 'resolved-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'dateAndTime',
			key: 'dateAndTime',
			width: '8%',
			responsive: ['lg'],
			render: (value, record) => {
				return (
					<div
						className='flex items-center  font-medium text-[#000]'
						style={{ color: record.isResolved ? 'green' : 'red' }}
					>
						{record.isResolved ? t<string>('yes') : t<string>('no')}
					</div>
				);
			},
		},
		{
			title: (
				<div className='  flex items-center cursor-pointer'>
					<ColumnTitle title={t('AssignedOn')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'createdAt-asc'))}
							style={{ color: sortBy === 'createdAt-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'createdAt-desc'))}
							style={{ color: sortBy === 'createdAt-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'dateAndTime',
			responsive: ['lg'],
			key: 'dateAndTime',
			render: (value, record) => {
				return (
					<div className=' text-[#01081E99] flex items-center'>{dayjs(record.createdAt).format(DATE_TIME_FORMAT)}</div>
				);
			},
		},
		...(isMobile
			? [
					{
						dataIndex: 'record',
						key: 'record',
						className: 'small-screen-row',
						render: (value, record) => {
							return (
								<div className='text-xs text-[#01081E99] flex flex-row xs:flex-col mb-1 w-100 justify-between items-center '>
									<div className='w-full'>
										<div className='flex flex-row py-2 justify-between'>
											<div> {t<string>('orderId')}:</div>
											<div>
												<Link
													to={`/order-life-cycle/?id=${record.orderNumber}`}
													className='text-xs text-[#01081E99] flex items-center mb-1 text-[#0ea5e9] underline'
												>
													{record.orderNumber}
												</Link>
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div> {t<string>('client')}:</div>
											<div className='text-lightBlue text-[15px] font-medium'>{record.clientFullName}</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div> {t<string>('merchant')}:</div>
											<div className='text-lightBlue text-[15px] font-medium'>{record.merchantFullName}</div>
										</div>

										{/* TODO: change the curency if needed to local currency */}
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('type')}:</div>
											<div className='mx-1 text-[15px] font-medium text-lightBlue flex items-center'>
												<>
													{record.type.toLocaleLowerCase() === OrderType.buy.toLocaleLowerCase() ? (
														<div className='text-[15px] font-medium text-[green] flex items-center'>
															{isDev ? t<string>('buy') : t<string>('deposit')}
														</div>
													) : (
														<div className='text-[15px] font-medium text-[red] flex items-center'>
															{isDev ? t<string>('sell') : t<string>('withdrawal')}
														</div>
													)}
												</>
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('amount')}:</div>
											<div className='text-[15px] font-medium text-lightBlue flex items-center'>
												{record.amount?.toLocaleString()}
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('price')}:</div>
											<div className='mx-1 text-[15px] font-medium text-lightBlue flex items-center'>
												{record.price}
												<span className='inline-block mx-1 text-[grey] text-[13px]'>USD</span>
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('Resolved')}:</div>
											<div
												className='flex items-center flex-wrap rounded-sm text-[14px] font-medium text-[#000] px-2 py-1'
												style={{ color: record.isResolved ? 'green' : 'red' }}
											>
												{record.isResolved ? t<string>('yes') : t<string>('no')}
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('status')}:</div>
											<div
												className='items-center flex flex-wrap rounded-sm text-[14px] font-medium text-[#000] px-2 py-1'
												style={{ color: getStatusColor(record.status).color }}
											>
												{/* <div
													style={{
														width: '8px',
														height: '8px',
														borderRadius: '100px',
														marginRight: '5px',
														backgroundColor: getStatusColor(record.status).color,
													}}
												/> */}
												{t<string>(record.status.toLocaleLowerCase())}
											</div>
										</div>
										<div className='flex flex-row py-2 justify-between'>
											<div>{t<string>('AssignedOn')}:</div>
											<div className='text-xs text-[15px] font-medium text-lightBlue  flex items-center '>
												{dayjs(record.createdAt).format(DATE_TIME_FORMAT)}
											</div>
										</div>
									</div>
								</div>
							);
						},
					} as TableColumnType<AppealTask>,
			  ]
			: []),
	];

	return columns;
};
